(() => {
  const copy = (key, fallback) => window.daelivoCartStrings?.[key] || fallback;
  if (window.themeVariables) Object.assign(window.themeVariables.strings, {
    shippingEstimatorNoResults: copy('shippingEstimatorNoResults', 'No shipping rates found for this address.'),
    shippingEstimatorOneResult: copy('shippingEstimatorOneResult', 'Shipping rate available:'),
    shippingEstimatorMultipleResults: copy('shippingEstimatorMultipleResults', 'Shipping rates available:'),
    shippingEstimatorError: copy('shippingEstimatorError', 'Please check your shipping address.')
  });
  const root = () => window.Shopify?.routes?.root || '/';
  // Stretch auto-opens on cart:change; a second show() resolves while that
  // first animation is still running. Keep the form busy until its native
  // completion event, or the shopper deliberately closes the drawer.
  const drawerOpenings = new WeakMap();
  document.addEventListener('dialog:before-show', event => {
    const drawer = event.target;
    if (drawer !== document.querySelector('cart-drawer#cart-drawer')) return;
    drawerOpenings.get(drawer)?.resolve();
    let resolve;
    const done = new Promise(complete => { resolve = complete; });
    drawerOpenings.set(drawer, { done, resolve });
  }, true);
  const settleDrawerOpening = event => {
    const opening = drawerOpenings.get(event.target);
    if (!opening) return;
    drawerOpenings.delete(event.target);
    opening.resolve();
  };
  document.addEventListener('dialog:after-show', settleDrawerOpening, true);
  document.addEventListener('dialog:before-hide', settleDrawerOpening, true);
  async function requestAdd(formData) {
    const controller = new AbortController();
    let timer;
    const request = (async () => {
      const response = await fetch(`${root()}cart/add.js`, { method: 'POST', body: formData, headers: { Accept: 'application/json' }, signal: controller.signal });
      const added = await response.json();
      if (!response.ok || added?.status) {
        const reason = added?.description || added?.message;
        const error = Error(reason || copy('addUnconfirmed', 'We could not confirm whether the item was added. Check your cart before adding it again.'));
        error.addServerMessage = !!reason;
        throw error;
      }
      if (!added || typeof added !== 'object' || !(added.id || (Array.isArray(added.items) && added.items.length))) throw Error('Unconfirmed add response');
      return added;
    })();
    try {
      return await Promise.race([request, new Promise((_, reject) => {
        timer = window.setTimeout(() => { controller.abort(); reject(Error('Add response timeout')); }, 15000);
      })]);
    } catch (error) {
      const failure = Error(error?.addServerMessage ? error.message : copy('addUnconfirmed', 'We could not confirm whether the item was added. Check your cart before adding it again.'));
      failure.addRequestFailed = true;
      failure.addServerMessage = error?.addServerMessage;
      throw failure;
    } finally { window.clearTimeout(timer); }
  }
  async function readCartJson(url, options = {}) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) throw Error(copy('refreshError', 'Your cart could not be refreshed. Please try again.'));
      return await response.json();
    } finally { window.clearTimeout(timer); }
  }
  function readCartCount(cart) {
    if (!Number.isInteger(cart?.item_count) || cart.item_count < 0) throw Error(copy('displayError', 'Your cart could not be displayed. Please try again.'));
    return cart;
  }
  const nativeSelections = new WeakMap();
  document.addEventListener('shopify:product:select', event => {
    const picker = event.target?.closest?.('variant-picker');
    if (!picker?.dataset.productId || !event.promise) return;
    const productId = String(picker.dataset.productId);
    const scope = picker.closest('product-card') || picker.closest('dialog') || picker.closest('.shopify-section');
    for (const component of scope?.querySelectorAll('product-form-component') || []) {
      if (String(component.dataset.productId) !== productId) continue;
      const form = component.querySelector('form[action*="/cart/add"]');
      if (!form) continue;
      const state = { pending: true, available: false, id: null, error: false };
      state.work = Promise.resolve(event.promise).then(result => {
        const detail = result?.detail, resource = detail?.resource;
        if (!resource?.id || (detail.productId && String(detail.productId) !== productId && !detail.newProduct)) throw Error('Selection unavailable');
        state.id = String(resource.id); state.available = resource.available !== false;
      }).catch(() => { state.error = true; }).finally(() => { state.pending = false; });
      nativeSelections.set(form, state);
    }
  });
  const selectionBlocked = form => {
    const state = nativeSelections.get(form);
    return !!state && (state.pending || state.error || !state.available);
  };
  async function waitForNativeSelection(form) {
    const deadline = Date.now() + 15000;
    while (true) {
      const state = nativeSelections.get(form);
      if (!state) return null;
      let timer;
      try {
        await Promise.race([state.work, new Promise((_, reject) => {
          timer = window.setTimeout(() => reject(Error(copy('selectionError', 'This option could not be loaded. Please select it again.'))), Math.max(1, deadline - Date.now()));
        })]);
      } finally { window.clearTimeout(timer); }
      if (nativeSelections.get(form) !== state) continue;
      if (state.error) throw Error(copy('selectionError', 'This option could not be loaded. Please select it again.'));
      if (!state.available) throw Error(copy('unavailableOption', 'This option is not available to order.'));
      return state;
    }
  }
  const syncCount = cart => {
    document.querySelectorAll('[data-cart-count], .header__cart-count, .cart-bubble__text-count').forEach(node => { node.textContent = String(cart.item_count); node.classList.toggle('hidden', !cart.item_count); });
    document.querySelectorAll('.cart-bubble').forEach(node => node.classList.toggle('visually-hidden', !cart.item_count));
  };
  async function refresh() {
    const url = new URL(root(), window.location.origin);
    url.searchParams.set('sections', 'cart-drawer-section');
    const sections = await readCartJson(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const next = new DOMParser().parseFromString(sections['cart-drawer-section'] || '', 'text/html').querySelector('cart-drawer');
    const drawer = document.querySelector('cart-drawer#cart-drawer');
    if (!next || !drawer) throw new Error(copy('displayError', 'Your cart could not be displayed. Please try again.'));
    drawer.replaceChildren(...next.children);
    window.DaelivoCartControls?.restoreFields();
    const cart = readCartCount(await readCartJson(`${root()}cart.js`, { headers: { Accept: 'application/json' } }));
    syncCount(cart); window.DaelivoCartControls?.restoreFields(cart);
    return drawer;
  }
  async function show() {
    let timer, finished = false;
    try {
      await Promise.race([(async () => {
        await customElements.whenDefined('cart-drawer');
        if (finished) return;
        const drawer = document.querySelector('cart-drawer#cart-drawer');
        if (!drawer) throw Error(copy('displayError', 'Your cart could not be displayed. Please try again.'));
        await drawer.show();
        let opening;
        while (!finished && (opening = drawerOpenings.get(drawer))) await opening.done;
      })(), new Promise((_, reject) => {
        timer = window.setTimeout(() => reject(Error(copy('displayError', 'Your cart could not be displayed. Please try again.'))), 15000);
      })]);
    } finally { finished = true; window.clearTimeout(timer); }
  }
  window.DaelivoCart = { refresh, show };
  document.addEventListener('cart:change', event => syncCount(event.detail.cart));
  document.addEventListener('cart-drawer:refreshed', event => {
    if (event.detail?.cart) syncCount(readCartCount(event.detail.cart));
  });
  // Window capture runs before Horizon's document-capture submit router.
  window.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action.includes('/cart/add')) return window.DaelivoCartControls?.beforeCheckout(event);
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!form.checkValidity()) { form.reportValidity(); return; }
    if (form.dataset.daelivoCartBusy === 'true') return;
    form.dataset.daelivoCartBusy = 'true';
    const buttons = [...form.querySelectorAll('[type="submit"]')];
    const disabledBefore = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; button.removeAttribute('data-added'); button.setAttribute('aria-busy', 'true'); });
    const previousError = form.querySelector('[data-daelivo-cart-error]');
    if (previousError) previousError.remove();
    const add = async () => { let confirmedAdd = false; try {
      await window.DaelivoQuickBuy?.waitForSelection(form);
      const selected = await waitForNativeSelection(form);
      if (!form.checkValidity()) { form.reportValidity(); return; }
      const formData = new FormData(form);
      if (selected && String(formData.get('id')) !== selected.id) throw Error(copy('selectionError', 'This option could not be loaded. Please select it again.'));
      formData.set('sections', 'cart-drawer-section');
      formData.set('sections_url', window.location.pathname);
      const added = await requestAdd(formData);
      confirmedAdd = true;
      if (!selectionBlocked(form) && String(new FormData(form).get('id')) === String(formData.get('id'))) {
        for (const component of form.querySelectorAll('add-to-cart-component')) component.animateAddToCart?.();
      }
      const cart = readCartCount(await readCartJson(`${root()}cart.js`, { headers: { Accept: 'application/json' } }));
      syncCount(cart);
      const quickBuy = form.closest('quick-buy-modal[data-daelivo-quick-buy]');
      if (quickBuy?.open) await quickBuy.hide();
      if (added.sections?.['cart-drawer-section']) {
        cart.sections = added.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      } else await refresh();
      await show();
    } catch (error) {
      const message = document.createElement('p');
      message.dataset.daelivoCartError = '';
      message.className = 'text-error';
      message.setAttribute('role', 'alert');
      message.textContent = confirmedAdd ? copy('addedRefreshError', 'The item was added. Open your cart to view it.') : error.message || copy('addError', 'This item could not be added. Please try again.');
      if (!confirmedAdd && error.addServerMessage) message.textContent += ' ' + copy('checkCartBeforeRetry', 'Check your cart before adding this item again.');
      if (confirmedAdd || error.addRequestFailed) {
        const link = document.createElement('a'); link.className = 'link';
        link.setAttribute('href', `${root()}cart`); link.textContent = error.addRequestFailed ? copy('viewCart', 'View cart') : copy('refreshCart', 'Refresh cart');
        message.append(' ', link);
      }
      form.append(message);
    } finally {
      form.dataset.daelivoCartBusy = 'false';
      buttons.forEach((button, index) => {
        button.disabled = form.dataset.daelivoQuickBuyError === 'true' || form.dataset.daelivoQuickBuyUnavailable === 'true' || selectionBlocked(form) || (nativeSelections.has(form) ? false : disabledBefore[index]);
        button.removeAttribute('aria-busy');
      });
    } };
    if (window.DaelivoCartControls?.queueMutation) await window.DaelivoCartControls.queueMutation(form, add);
    else await add();
  }, true);
})();

// Keep Stretch's markup, widgets and motion; recover cart controls from rejected
// requests and stop field Enter keys from implicitly submitting checkout.
(() => {
  const copy = (key, fallback) => window.daelivoCartStrings?.[key] || fallback;
  const root = () => window.Shopify?.routes?.root || '/';
  const active = new WeakMap(), failures = new Map(), pending = new Set(), resumed = new WeakSet(), checkoutWaiting = new Set();
  let mutationTail = Promise.resolve();
  let knownEmptyCart = false;
  let reloadRequested = false;
  const controlStates = new Map();
  const pageSection = 'daelivo-cart-page-state';
  const emit = (type, detail) => document.documentElement.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
  const applicableCodes = cart => (cart.discount_codes || []).filter(code => code.applicable).map(code => code.code);
  const messageFrom = data => data?.description || data?.message || Object.values(data?.errors || data || {}).flat().filter(value => typeof value === 'string').join('. ');

  async function requestJson(path, options = {}, timeout = 15000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(path, { ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok) {
        const error = Error(messageFrom(data) || copy('requestError', 'The request could not be completed. Please try again.'));
        error.serverMessage = true;
        error.serverReasons = data?.description || data?.message ? [messageFrom(data)] : Object.values(data?.errors || data || {}).flat().filter(value => typeof value === 'string');
        throw error;
      }
      return data;
    } finally { window.clearTimeout(timer); }
  }

  function errorRegion(owner) {
    if (owner.tagName?.toLowerCase() === 'line-item') {
      const region = owner.querySelector('.line-item__error-container');
      if (region) return region;
    }
    if (owner.tagName?.toLowerCase() === 'cart-discount-field' || owner.getAttribute('discount-code')) {
      const banner = owner.closest('.cart-discount')?.querySelector('cart-discount-banner') || owner.parentElement?.querySelector('cart-discount-banner');
      if (banner) return banner;
    }
    let region = owner.querySelector('[data-daelivo-control-error]');
    if (!region) {
      region = document.createElement('p'); region.dataset.daelivoControlError = ''; region.className = 'text-error'; region.setAttribute('role', 'alert'); owner.append(region);
    }
    return region;
  }
  function showError(owner, message, { blocksCheckout = true } = {}) {
    const region = errorRegion(owner);
    if (region.tagName?.toLowerCase() === 'cart-discount-banner') {
      const text = document.createElement('p'); text.className = 'text-error'; text.setAttribute('role', 'alert'); text.textContent = message; region.replaceChildren(text);
    } else region.textContent = message;
    region.hidden = false;
    if (blocksCheckout) failures.set(owner, message);
    else failures.delete(owner);
  }
  function clearError(owner) {
    failures.delete(owner);
    const region = owner.querySelector('[data-daelivo-control-error]') || owner.querySelector('.line-item__error-container') || owner.closest('.cart-discount')?.querySelector('cart-discount-banner');
    if (region) region.hidden = true;
  }
  // The source replaces drawer children twice. Input IDs survive both swaps;
  // node references do not. Keep accepted field work attached to that identity.
  function controlState(input) { return input?.id ? controlStates.get(input.id) : null; }
  function adoptControl(state) {
    const input = state.id ? document.getElementById(state.id) : state.input;
    const owner = input?.closest(state.tag);
    if (!owner?.isConnected) return state.owner;
    if (!state.bindings.has(input)) {
      state.bindings.set(input, { owner, disabledBefore: !!input.disabled });
      input.value = state.value;
      if (!state.pending && state.kind === 'note' && !state.message) input.defaultValue = state.value;
      if (state.codes !== undefined) {
        const hidden = owner.querySelector('[name="discount"]'); if (hidden) hidden.value = state.codes;
      }
      if (state.message) showError(owner, state.message, { blocksCheckout: state.blocksCheckout });
    }
    if (state.pending) { input.disabled = true; owner.setAttribute('aria-busy', 'true'); }
    state.input = input; state.owner = owner;
    return owner;
  }
  function syncConfirmedNotes(cart) {
    if (typeof cart?.note !== 'string') return;
    for (const state of controlStates.values()) {
      if (state.kind !== 'note' || state.pending || state.message) continue;
      state.value = cart.note;
      for (const [input] of state.bindings) {
        if (!input.disabled && input.value === input.defaultValue) { input.value = cart.note; input.defaultValue = cart.note; }
      }
    }
  }
  function restoreControls(cart) { syncConfirmedNotes(cart); for (const state of controlStates.values()) adoptControl(state); }
  function discardControl(input) {
    const previous = controlState(input);
    if (!previous) return;
    for (const binding of previous.bindings.values()) clearError(binding.owner);
    controlStates.delete(previous.id);
  }
  function startControl(kind, owner, input, value) {
    discardControl(input);
    const state = { kind, tag: kind === 'note' ? 'cart-note' : 'cart-discount-field', id: input?.id,
      input, owner, value, pending: true, bindings: new Map(), message: '', blocksCheckout: false };
    // Remove buttons without a field still use the same queue/error recovery.
    if (input) {
      state.bindings.set(input, { owner, disabledBefore: !!input.disabled });
      if (state.id) controlStates.set(state.id, state);
      input.disabled = true;
    }
    owner.setAttribute('aria-busy', 'true');
    return state;
  }
  function controlError(state, message, blocksCheckout = true) {
    state.message = message; state.blocksCheckout = blocksCheckout;
    showError(adoptControl(state), message, { blocksCheckout });
  }
  function finishControl(state, confirmed) {
    adoptControl(state); state.pending = false;
    for (const [input, binding] of state.bindings) {
      input.disabled = binding.disabledBefore; binding.owner.setAttribute('aria-busy', 'false');
      if (confirmed && state.kind === 'note') input.defaultValue = state.value;
    }
    state.owner.setAttribute('aria-busy', 'false');
    // Keep a confirmed note through a delayed second source swap. Later cart
    // events supply its authoritative note, without overwriting a current draft.
    if (confirmed && state.kind === 'discount' && state.id) controlStates.delete(state.id);
    for (const [input, binding] of state.bindings) if (!binding.owner.isConnected) state.bindings.delete(input);
  }
  function hasBlockingFailure() {
    for (const owner of failures.keys()) if (!owner.isConnected) failures.delete(owner);
    return failures.size > 0 || [...controlStates.values()].some(state => state.blocksCheckout);
  }
  function flushReload() {
    if (!reloadRequested || pending.size || (checkoutWaiting.size && !knownEmptyCart) || hasBlockingFailure()) return;
    reloadRequested = false; window.location.reload();
  }
  function refreshMainCart(cart) {
    if (window.themeVariables?.settings?.pageType !== 'cart') return;
    const current = document.querySelector('.shopify-section--main-cart');
    if (!current) return;
    const html = cart.sections?.[pageSection];
    const next = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section--main-cart');
    if (!next) {
      const error = Error(copy('displayError', 'Your cart could not be displayed. Please try again.'));
      error.serverMessage = true; error.cartRefreshRequired = true; throw error;
    }
    // The exact server view keeps line prices, promotions and currency in sync
    // while later accepted note work prevents a navigation. Stable field work
    // is rebound immediately, just as it is after the source drawer swaps.
    current.replaceWith(next); restoreControls();
  }
  function requestReload(owner) { clearError(owner); reloadRequested = true; flushReload(); }
  function queueUpdate(owner, operation) {
    if (active.has(owner)) return active.get(owner);
    clearError(owner);
    const work = mutationTail.then(operation).catch(() => false);
    mutationTail = work; active.set(owner, work); pending.add(work);
    work.finally(() => {
      active.delete(owner); pending.delete(work); flushReload(); });
    return work;
  }

  function changeQuantity(owner, lineKey, targetQuantity) {
    if (active.has(owner)) return active.get(owner);
    const input = owner.querySelector('[data-line-key]');
    if (!Number.isInteger(targetQuantity) || targetQuantity < 0 || !lineKey) {
      showError(owner, copy('quantityInvalid', 'Enter a valid quantity.'));
      if (input) input.value = input.defaultValue;
      return Promise.resolve(false);
    }
    const controls = [...owner.querySelectorAll('input, button, a[href*="/cart/change"]')];
    const disabledBefore = controls.map(control => !!control.disabled);
    owner.setAttribute('aria-busy', 'true');
    controls.forEach(control => { control.disabled = true; control.setAttribute('aria-disabled', 'true'); });
    return queueUpdate(owner, async () => {
      emit('theme:loading:start');
      owner.dispatchEvent(new CustomEvent('line-item:will-change', {bubbles:true, detail:{targetQuantity}}));
      try {
        const sections = new Set(['cart-drawer-section']);
        if (window.themeVariables?.settings?.pageType === 'cart') sections.add(pageSection);
        emit('cart:prepare-bundled-sections', {sections});
        const cart = await requestJson(`${root()}cart/change.js`, {method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({id:lineKey, quantity:targetQuantity, sections:[...sections].join(','), sections_url:window.location.pathname})});
        knownEmptyCart = cart.item_count === 0;
        let confirmedLine = cart.items.find(item => item.key === lineKey);
        if (!confirmedLine && targetQuantity > 0) {
          // Shopify changes keys when discount allocations change. A unique
          // variant is safe to identify; property/subscription duplicates are not.
          const variantId = lineKey.split(':')[0];
          const candidates = cart.items.filter(item => String(item.variant_id || item.id || item.key.split(':')[0]) === variantId);
          if (candidates.length !== 1) {
            const error = Error(copy('cartChanged', 'Your cart changed. Refresh your cart to confirm the updated quantity.'));
            error.serverMessage = true; error.cartRefreshRequired = true; throw error;
          }
          confirmedLine = candidates[0];
        }
        const quantity = confirmedLine?.quantity || 0;
        const previousQuantity = Number(input?.defaultValue);
        if (confirmedLine) {
          owner.querySelectorAll('a[href*="/cart/change"]').forEach(link => {
            const url = new URL(link.href, window.location.origin);
            const previousTarget = Number(url.searchParams.get('quantity'));
            url.searchParams.set('id', confirmedLine.key);
            url.searchParams.set('quantity', String(link.closest('quantity-selector') ? Math.max(0, quantity + previousTarget - previousQuantity) : 0));
            link.href = url.href;
          });
        }
        owner.querySelectorAll('[data-line-key]').forEach(field => {
          field.value = String(quantity); field.defaultValue = String(quantity);
          if (confirmedLine) field.setAttribute('data-line-key', confirmedLine.key);
        });
        owner.dispatchEvent(new CustomEvent('line-item:change', {bubbles:true, detail:{quantity, cart}}));
        emit('cart:change', {baseEvent:'line-item:change', cart});
        // The source reloads /cart after an edit. If checkout is already waiting,
        // its existing footer form can submit the now-confirmed server cart.
        if (window.themeVariables?.settings?.pageType === 'cart') { refreshMainCart(cart); reloadRequested = true; }
        return true;
      } catch (error) {
        if (input) input.value = input.defaultValue;
        const errorOwner = error.cartRefreshRequired && !owner.isConnected ? document.querySelector('.shopify-section--main-cart') || owner : owner;
        showError(errorOwner, error.serverMessage ? error.message : copy('quantityError', 'The quantity could not be updated. Please try again.'));
        const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'link text-sm'; retry.textContent = error.cartRefreshRequired ? copy('refreshCart', 'Refresh cart') : copy('retry', 'Retry');
        retry.addEventListener('click', () => error.cartRefreshRequired ? requestReload(errorOwner) : changeQuantity(owner, lineKey, targetQuantity));
        errorRegion(errorOwner).append(retry);
        return false;
      } finally {
        controls.forEach((control, index) => { control.disabled = disabledBefore[index]; control.removeAttribute('aria-disabled'); });
        owner.setAttribute('aria-busy', 'false'); emit('theme:loading:end');
      }
    });
  }

  function applyDiscount(owner, value = '', removeCode = '') {
    const input = owner.querySelector('input[type="text"]');
    const previousState = controlState(input);
    if (previousState?.pending) return previousState.work;
    if (active.has(owner)) return active.get(owner);
    const state = startControl('discount', owner, input, value);
    let confirmed = false;
    state.work = queueUpdate(owner, async () => {
      try {
        const previous = await requestJson(`${root()}cart.js`, { cache: 'no-store' });
        const codes = applicableCodes(previous).filter(code => code.toLowerCase() !== removeCode.toLowerCase());
        if (value.trim()) codes.push(...value.split(',').map(code => code.trim()).filter(Boolean));
        const uniqueCodes = [...new Map(codes.map(code => [code.toLowerCase(), code])).values()];
        const cart = await requestJson(`${root()}cart/update.js`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: uniqueCodes.join(','), sections: window.themeVariables?.settings?.pageType === 'cart' ? `cart-drawer-section,${pageSection}` : 'cart-drawer-section', sections_url: window.location.pathname }) });
        state.codes = uniqueCodes.join(',');
        const hidden = adoptControl(state).querySelector('[name="discount"]'); if (hidden) hidden.value = state.codes;
        if ((cart.discount_codes || []).some(code => !code.applicable)) {
          // Shopify confirmed the cart; an optional code being ineligible must
          // not block checkout at the confirmed price. Failed requests still do.
          controlError(state, copy('discountInvalid', 'Enter a valid discount code. This code may be expired or unavailable for your order.'), false);
          emit('cart:discount:error'); return false;
        }
        // Clearing an invalid code or reapplying the same code must not tear down
        // another open footer panel when the effective cart has not changed.
        confirmed = true;
        if (cart.total_price === previous.total_price && applicableCodes(cart).join(',').toLowerCase() === applicableCodes(previous).join(',').toLowerCase()) return true;
        if (window.themeVariables?.settings?.pageType === 'cart') { refreshMainCart(cart); reloadRequested = true; }
        else if (cart.sections?.['cart-drawer-section']) emit('cart:change', { baseEvent: 'discount:change', cart });
        else await window.DaelivoCart?.refresh();
        return true;
      } catch (error) {
        confirmed = false; controlError(state, copy('discountError', 'The discount could not be checked. Please try again.')); return false;
      } finally { finishControl(state, confirmed); }
    });
    return state.work;
  }

  function saveNote(owner) {
    const input = owner.querySelector('[name="note"]');
    const previousState = controlState(input);
    if (previousState?.pending) return previousState.work;
    if (active.has(owner)) return active.get(owner);
    if (!input) return Promise.resolve(true);
    const note = input.value;
    if (note === input.defaultValue) { discardControl(input); clearError(owner); flushReload(); return Promise.resolve(true); }
    const state = startControl('note', owner, input, note);
    let confirmed = false;
    state.work = queueUpdate(owner, async () => {
      try {
        const cart = await requestJson(`${root()}cart/update.js`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
        if (String(cart.note || '') !== note) throw Error('Note not confirmed');
        adoptControl(state); state.input.defaultValue = note;
        confirmed = true;
        document.querySelectorAll('cart-note [name="note"]').forEach(other => {
          if (other !== input && !other.disabled && other.value === other.defaultValue) { other.value = note; other.defaultValue = note; }
        });
        return true;
      } catch (error) { controlError(state, copy('noteError', 'Your order note could not be saved. Please try again.')); return false; }
      finally { finishControl(state, confirmed); }
    });
    return state.work;
  }

  async function estimateShipping(owner) {
    if (owner.dataset.daelivoEstimateBusy === 'true') return false;
    const button = owner.querySelector('[type="button"]'), results = owner.querySelector('[aria-live="polite"]');
    if (!button || !results) return false;
    owner.dataset.daelivoEstimateBusy = 'true'; button.disabled = true; button.setAttribute('aria-busy', 'true');
    results.replaceChildren(); results.hidden = true;
    const deadline = Date.now() + 15000;
    // Shopify polls the calculation prepared for this exact accepted address.
    const address = ['zip', 'country', 'province'].map(key => [key, owner.querySelector(`[name="address[${key}]"]`)?.value.trim() || '']);
    const url = endpoint => {
      const addressUrl = new URL(`${root()}cart/${endpoint}.json`, window.location.origin);
      for (const [key, value] of address) addressUrl.searchParams.set(`shipping_address[${key}]`, value);
      return addressUrl;
    };
    try {
      await requestJson(url('prepare_shipping_rates'), { method: 'POST' }, Math.max(1, deadline - Date.now()));
      let data = null;
      for (let attempt = 0; attempt < 20 && Date.now() < deadline; attempt++) {
        data = await requestJson(url('async_shipping_rates'), {}, Math.max(1, deadline - Date.now()));
        if (data !== null) break;
        await new Promise(resolve => window.setTimeout(resolve, 750));
      }
      if (!Array.isArray(data?.shipping_rates)) throw Error('Rates not ready');
      const rates = data.shipping_rates, strings = window.themeVariables?.strings || {};
      const stack = document.createElement('div'); stack.className = 'v-stack gap-2';
      const text = document.createElement('p');
      text.textContent = rates.length === 0 ? (strings.shippingEstimatorNoResults || copy('shippingEstimatorNoResults', 'No shipping rates found for this address.')) : rates.length === 1 ? (strings.shippingEstimatorOneResult || copy('shippingEstimatorOneResult', 'Shipping rate available:')) : (strings.shippingEstimatorMultipleResults || copy('shippingEstimatorMultipleResults', 'Shipping rates available:'));
      const list = document.createElement('ul'); list.className = 'list-disc'; list.setAttribute('role', 'list');
      rates.forEach(rate => { const item = document.createElement('li'); item.textContent = `${rate.presentment_name || rate.name}: ${rate.currency || window.Shopify?.currency?.active || ''} ${rate.price}`; list.append(item); });
      stack.append(text, list); results.append(stack);
      return true;
    } catch (error) {
      const stack = document.createElement('div'); stack.className = 'v-stack gap-1 text-error'; stack.setAttribute('role', 'alert');
      const text = document.createElement('p');
      text.textContent = error.serverMessage ? (window.themeVariables?.strings?.shippingEstimatorError || copy('shippingEstimatorError', 'Please check your shipping address.')) : copy('shippingError', 'Shipping could not be estimated. Check the address and try again.');
      stack.append(text);
      if (error.serverMessage) {
        const list = document.createElement('ul'); list.className = 'list-disc'; list.setAttribute('role', 'list');
        for (const reason of error.serverReasons?.length ? error.serverReasons : [error.message]) { const item = document.createElement('li'); item.textContent = reason; list.append(item); }
        stack.append(list);
      }
      results.replaceChildren(stack); return false;
    } finally { results.hidden = false; button.disabled = false; button.setAttribute('aria-busy', 'false'); owner.dataset.daelivoEstimateBusy = 'false'; }
  }

  const waitForUpdates = async () => { while (pending.size) await Promise.all([...pending]); };
  async function beforeCheckout(event) {
    if (event.submitter?.name !== 'checkout') return;
    const form = event.target;
    if (resumed.has(form)) { resumed.delete(form); return; }
    if (checkoutWaiting.has(form)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    restoreControls();
    if (!pending.size && !hasBlockingFailure() && !knownEmptyCart) return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkoutWaiting.add(form);
    const wasDisabled = !!event.submitter.disabled;
    event.submitter.disabled = true; event.submitter.setAttribute?.('aria-busy', 'true');
    try {
      await waitForUpdates();
      restoreControls();
      if (hasBlockingFailure() || knownEmptyCart) return;
      const currentForm = form.isConnected ? form : document.getElementById(form.id);
      if (!currentForm?.isConnected) return;
      const submitter = event.submitter.isConnected ? event.submitter : currentForm.querySelector('[name="checkout"]');
      if (!submitter) return;
      submitter.disabled = false; submitter.removeAttribute?.('aria-busy');
      reloadRequested = false;
      resumed.add(currentForm); currentForm.requestSubmit(submitter);
    } finally {
      checkoutWaiting.delete(form);
      event.submitter.disabled = wasDisabled; event.submitter.removeAttribute?.('aria-busy');
      flushReload();
    }
  }
  window.DaelivoCartControls = { applyDiscount, saveNote, estimateShipping, changeQuantity, queueMutation:queueUpdate, waitForUpdates, beforeCheckout, restoreFields:restoreControls };
  document.addEventListener('cart-drawer:refreshed', restoreControls);
  document.addEventListener('cart:change', event => {
    knownEmptyCart = event.detail.cart.item_count === 0;
    syncConfirmedNotes(event.detail.cart);
    Promise.resolve().then(restoreControls);
  });

  window.addEventListener('change', event => {
    const lineKey = event.target.getAttribute?.('data-line-key'), line = event.target.closest?.('line-item');
    if (line && lineKey) { event.stopImmediatePropagation(); changeQuantity(line, lineKey, Number(event.target.value)); return; }
    const discount = event.target.closest?.('cart-discount-field');
    if (discount) { event.stopImmediatePropagation(); applyDiscount(discount, event.target.value); return; }
    const note = event.target.closest?.('cart-note');
    if (note && event.target.name === 'note') { event.stopImmediatePropagation(); saveNote(note); }
  }, true);
  window.addEventListener('keydown', event => {
    if (event.key !== 'Enter') return;
    const field = event.target.closest?.('cart-discount-field');
    const estimator = event.target.closest?.('shipping-estimator');
    if (field) { event.preventDefault(); event.stopImmediatePropagation(); applyDiscount(field, event.target.value); event.target.blur(); }
    else if (estimator && event.target.name === 'address[zip]') { event.preventDefault(); event.stopImmediatePropagation(); estimateShipping(estimator); }
  }, true);
  window.addEventListener('click', async event => {
    const changeLink = event.target.closest?.('line-item a[href*="/cart/change"]');
    if (changeLink) {
      event.preventDefault(); event.stopImmediatePropagation();
      const url = new URL(changeLink.href, window.location.origin);
      return changeQuantity(changeLink.closest('line-item'), url.searchParams.get('id'), Number(url.searchParams.get('quantity')));
    }
    const button = event.target.closest?.('button');
    const estimator = button?.closest('shipping-estimator');
    if (estimator) { event.preventDefault(); event.stopImmediatePropagation(); await estimateShipping(estimator); return; }
    const remove = event.target.closest?.('cart-discount-remove-button');
    if (remove) { event.preventDefault(); event.stopImmediatePropagation(); const field = remove.closest('.cart-discount')?.querySelector('cart-discount-field'); await applyDiscount(field || remove, '', remove.getAttribute('discount-code')); return; }
    const popover = button?.closest('x-drawer-popover');
    const note = popover?.querySelector('cart-note');
    if (note && button.closest('dialog-close-button')) {
      event.preventDefault(); event.stopImmediatePropagation(); button.disabled = true; button.setAttribute('aria-busy', 'true');
      try { if (await saveNote(note)) await popover.hide(); }
      finally { button.disabled = false; button.setAttribute('aria-busy', 'false'); }
    }
  }, true);
})();
